<!--
  @component
  Generates an SVG Beeswarm chart using a [d3-force simulation](https://github.com/d3/d3-force).
 -->
<script>
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import { getContext, untrack } from "svelte";
  import { forceSimulation, forceX, forceY, forceCollide } from "d3-force";

  const { data, xGet, zGet, width, height, custom } = getContext("LayerCake");

  /**
   * @typedef {Object} Props
   * @property {number} [r=4] - The circle radius size in pixels.
   * @property {number} [strokeWidth=1] - The circle's stroke width in pixels.
   * @property {string} [stroke='#fff'] - The circle's stroke color.
    * @property {boolean} [showMidline=false] - Whether to draw a horizontal guide line at the vertical center.
    * @property {string} [midlineStroke='#d9d9d9'] - The midline stroke color.
    * @property {number} [midlineStrokeWidth=1] - The midline stroke width in pixels.
   * @property {number} [xStrength=0.95] - The value passed into the `.strength` method on `forceX`. See [the documentation](https://github.com/d3/d3-force#x_strength).
   * @property {number} [yStrength=0.075] - The value passed into the `.strength` method on `forceY`. See [the documentation](https://github.com/d3/d3-force#y_strength).
   * @property {Object} [tooltip] - The tooltip data.
   */

  /** @type {Props} */
  let {
    r = 4,
    strokeWidth = 0,
    stroke = "none",
    showMidline = false,
    midlineStroke = "#d9d9d9",
    midlineStrokeWidth = 1,
    xStrength = 0.95,
    yStrength = 0.075,
    tooltip = $bindable()
  } = $props();

  /** @type {any[]} */
  let nodes = $state([]);

  let simulation = $derived.by(() => {
    if (!$width || !$height || !$data.length) return null;

    const sim = forceSimulation($data.map((/** @type {any} */ d) => ({ ...d })))
      .force(
        "x",
        forceX()
          .x((d) => $xGet(d))
          .strength(xStrength)
      )
      .force(
        "y",
        forceY()
          .y($height / 2)
          .strength(yStrength)
      )
      .force("collide", forceCollide(r))
      .stop();

    return sim;
  });

  $effect(() => {
    if (!simulation) {
      nodes = [];
      return;
    }

    untrack(() => {
      // Run the simulation for a fixed number of iterations
      const maxIterations = Math.ceil(
        Math.log(simulation.alphaMin()) / Math.log(1 - simulation.alphaDecay())
      );

      for (let i = 0; i < maxIterations; ++i) {
        simulation.tick();
      }

      // Update nodes state to trigger reactivity
      nodes = [...simulation.nodes()];
    });
  });
</script>

<g class="bee-group">
  {#if showMidline}
    <line
      x1={0}
      x2={$width}
      y1={$height / 2}
      y2={$height / 2}
      stroke={midlineStroke}
      stroke-width={midlineStrokeWidth}
      pointer-events="none"
    ></line>
  {/if}
  {#each nodes.filter((d) => $custom.selectedID == d.DistrictID) as node}
    <circle
      fill={urbanColors.gray_shade_lighter}
      stroke="none"
      stroke-width=0
      cx={node.x}
      cy={node.y}
      r={20}
    ></circle>
  {/each}
  {#each nodes as node}
    <circle
      fill={$custom.groupType ? $zGet(node) : $custom.seriesColors[0]}
      {stroke}
      stroke-width={strokeWidth}
      cx={node.x}
      cy={node.y}
      {r}
      role="button"
      tabindex="-1"
      onmousemove={(e) =>
        (tooltip = {
          x: e.pageX,
          y: e.pageY,
          data: node,
          color: $custom.groupType ? $zGet(node) : $custom.seriesColors[0]
        })}
      onmouseleave={() => (tooltip = undefined)}
      onclick={() => $custom.onClick(node)}
      onkeydown={null}
    ></circle>
  {/each}
</g>

<style>
  .bee-group circle[role="button"]:focus,
  .bee-group circle[role="button"]:focus-visible {
    outline: none;
  }
</style>
